import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Send,
  Smartphone,
  Globe,
  Bot,
  ClipboardList,
  RefreshCw,
  Compass,
  Paperclip,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { uploadChatAttachment } from "@/services/storage";
import { useAuth } from "@/contexts/AuthContext";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface StudentContext {
  summary: string;
  persona: Record<string, unknown> | null;
  aiDirection: string;
  directionUpdatedAt: string | null;
  counselorInstruction: string;
}

interface ChatMessage {
  id: string;
  sessionId?: string;
  senderId: string;
  message: string;
  messageType?: string;
  fileId?: string | null;
  fileUrl?: string | null; // resolved short-lived download URL for attachments
  sentAt?: string;
  timestamp?: string; // API returns 'timestamp' not 'sentAt'
  senderLabel?: string;
  sender?: string; // API returns sender as string name, not object
}

interface PendingAttachment {
  fileId: string;
  fileName: string;
  previewUrl?: string; // object URL for image thumbnails
  isImage: boolean;
}

interface AiChatSession {
  id: string;
  studentId: string;
  status: string;
  isManualMode: boolean;
  channel: "web" | "whatsapp";
  startedAt: string;
  student: { id: string; name: string; phoneNumber: string } | null;
  latestMessage: { message: string; sentAt: string; senderId: string } | null;
  lastStudentMessageAt: string | null;
}


const isOutside24hWindow = (s: {
  channel: "web" | "whatsapp";
  lastStudentMessageAt: string | null;
}) =>
  s.channel === "whatsapp" &&
  (!s.lastStudentMessageAt ||
    Date.now() - new Date(s.lastStudentMessageAt).getTime() > 24 * 60 * 60 * 1000);

interface Props {
  session: AiChatSession;
  onClose: () => void;
  onManualModeChange: (id: string, val: boolean) => void;
}

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none",
      checked ? "bg-orange-500" : "bg-gray-300",
      disabled && "opacity-50 cursor-not-allowed",
    )}
  >
    <span
      className={cn(
        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
        checked ? "translate-x-6" : "translate-x-1",
      )}
    />
  </button>
);

const AiSessionChatModal: React.FC<Props> = ({ session, onClose, onManualModeChange }) => {
  const { user } = useAuth();
  const canSendManually = session.isManualMode && !isOutside24hWindow(session);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [togglingMode, setTogglingMode] = useState(false);
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);

  // Per-student steering panel
  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState<StudentContext | null>(null);
  const [instructionDraft, setInstructionDraft] = useState("");
  const [savingInstruction, setSavingInstruction] = useState(false);
  const [refreshingDirection, setRefreshingDirection] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const data = await api.get(`/admin/ai-chat-sessions/${session.id}/messages`);
      const msgs = Array.isArray(data) ? data : (data?.data ?? data?.messages ?? []);
      setMessages(msgs);
    } catch {
      // keep existing messages on error
    } finally {
      setLoadingMessages(false);
    }
  }, [session.id]);

  const fetchContext = useCallback(async () => {
    try {
      const data = await api.get<StudentContext>(
        `/admin/ai-chat-sessions/${session.id}/student-context`,
      );
      setContext(data);
      setInstructionDraft(data.counselorInstruction ?? "");
    } catch {
      // leave context null on error
    }
  }, [session.id]);

  // Initial load + 3-second polling
  useEffect(() => {
    void fetchMessages();
    void fetchContext();
    const interval = setInterval(() => void fetchMessages(), 3000);
    return () => clearInterval(interval);
  }, [fetchMessages, fetchContext]);

  const handleSaveInstruction = async () => {
    setSavingInstruction(true);
    try {
      await api.put(`/admin/ai-chat-sessions/${session.id}/counselor-instruction`, {
        instruction: instructionDraft,
      });
      setContext((prev) =>
        prev ? { ...prev, counselorInstruction: instructionDraft.trim() } : prev,
      );
    } catch {
      // silently fail — user can retry
    } finally {
      setSavingInstruction(false);
    }
  };

  const handleRefreshDirection = async () => {
    setRefreshingDirection(true);
    try {
      const data = await api.post<StudentContext>(
        `/admin/ai-chat-sessions/${session.id}/refresh-direction`,
      );
      setContext(data);
    } catch {
      // silently fail — user can retry
    } finally {
      setRefreshingDirection(false);
    }
  };

  const formatRefreshedAt = (iso: string | null): string => {
    if (!iso) return "never";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "never";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Scroll to bottom only when user is already near the bottom,
  // so polling updates don't yank them away while reading older messages.
  useEffect(() => {
    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 80;
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const ALLOWED_EXT = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
    "text/plain",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];
  const MAX_FILE_BYTES = 10 * 1024 * 1024;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later.
    if (e.target) e.target.value = "";
    if (!file) return;
    setFileError(null);

    if (!ALLOWED_EXT.includes(file.type)) {
      setFileError("Unsupported file type. Use an image, PDF, DOC, or XLS.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError("File must be smaller than 10 MB.");
      return;
    }

    setUploadingFile(true);
    try {
      const { fileId, fileName } = await uploadChatAttachment(session.id, file);
      const isImage = file.type.startsWith("image/");
      setAttachment({
        fileId,
        fileName,
        isImage,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      });
    } catch {
      setFileError("Upload failed. Please try again.");
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = () => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
    setFileError(null);
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !attachment) || !canSendManually || sending) return;
    setSending(true);
    setSendError(null);
    try {
      await api.post(`/admin/ai-chat-sessions/${session.id}/messages`, {
        message: trimmed,
        ...(attachment ? { fileId: attachment.fileId } : {}),
      });
      setText("");
      removeAttachment();
      await fetchMessages();
    } catch (err: any) {
      setSendError(
        err?.message ||
          "Failed to send. It may be outside the 24h WhatsApp window.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleToggle = async (val: boolean) => {
    setTogglingMode(true);
    try {
      onManualModeChange(session.id, val);
    } finally {
      setTogglingMode(false);
    }
  };

  const isAdminMessage = (msg: ChatMessage): boolean => {
    return msg.senderId === user?.id || !!msg.senderLabel;
  };

  const isStudentMessage = (msg: ChatMessage): boolean => {
    return msg.senderId === session.studentId;
  };

  const getMessageTime = (msg: ChatMessage): string => {
    const raw = msg.timestamp || msg.sentAt;
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  // WhatsApp-style day key: Today / Yesterday / weekday name (up to 6 days
  // back) / dd/MM/yyyy beyond that.
  const getDateKey = (msg: ChatMessage): string => {
    const raw = msg.timestamp || msg.sentAt;
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const getDateHeaderLabel = (raw: string): string => {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    const startOfDay = (dt: Date) =>
      new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
    const dayDiff = Math.round(
      (startOfDay(new Date()) - startOfDay(d)) / (24 * 60 * 60 * 1000),
    );
    if (dayDiff === 0) return "Today";
    if (dayDiff === 1) return "Yesterday";
    if (dayDiff > 1 && dayDiff < 7) {
      return d.toLocaleDateString("en-IN", { weekday: "long" });
    }
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  const formatMarkdown = (text: string) => {
    if (!text) return text;
    // Convert markdown to simple inline elements
    const parts: (string | React.ReactElement)[] = [];
    // Split by **bold**, *italic*, `code`, and newlines
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|#{1,3}\s+(.+))/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    const processed = text
      // Headers → bold
      .replace(/^#{1,6}\s+(.+)$/gm, '**$1**');

    while ((match = regex.exec(processed)) !== null) {
      if (match.index > lastIndex) {
        parts.push(processed.slice(lastIndex, match.index));
      }
      if (match[2]) {
        parts.push(<strong key={key++}>{match[2]}</strong>);
      } else if (match[3]) {
        parts.push(<em key={key++}>{match[3]}</em>);
      } else if (match[4]) {
        parts.push(<code key={key++} className="bg-black/10 rounded px-1 text-xs">{match[4]}</code>);
      } else {
        parts.push(match[0]);
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < processed.length) {
      parts.push(processed.slice(lastIndex));
    }
    return parts.length > 0 ? <>{parts}</> : text;
  };

  // Render an image thumbnail / file chip for attachment messages, else text.
  // The backend stores a "[Attachment]" placeholder when there is no caption,
  // so hide that sentinel rather than showing it as body text.
  const renderMessageBody = (msg: ChatMessage, isAdmin: boolean) => {
    const isAttachment = msg.messageType === "image" || msg.messageType === "file";
    const hasCaption = !!msg.message && msg.message !== "[Attachment]";

    if (!isAttachment) return formatMarkdown(msg.message);

    return (
      <div className="flex flex-col gap-2">
        {msg.messageType === "image" ? (
          msg.fileUrl ? (
            <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={msg.fileUrl}
                alt={hasCaption ? msg.message : "attachment"}
                className="max-h-60 max-w-full rounded-lg object-cover"
              />
            </a>
          ) : (
            <span className="text-xs italic opacity-80">Image unavailable</span>
          )
        ) : (
          <a
            href={msg.fileUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-2 rounded-lg px-3 py-2",
              isAdmin ? "bg-white/20" : "bg-black/5",
              !msg.fileUrl && "pointer-events-none opacity-70",
            )}
          >
            <FileText className="h-5 w-5 shrink-0" />
            <span className="truncate text-sm underline">
              {msg.fileUrl ? "Open attachment" : "Attachment"}
            </span>
          </a>
        )}
        {hasCaption && <span>{formatMarkdown(msg.message)}</span>}
      </div>
    );
  };

  return (
    // Full-bleed on phones — a chat thread inset inside a backdrop wastes the
    // little vertical space a mobile keyboard leaves.
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-xl w-full max-w-2xl h-full sm:h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-200">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <div className="hidden sm:flex h-9 w-9 shrink-0 rounded-full bg-primary items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900 truncate">
                  {session.student?.name ?? "Unknown Student"}
                </span>
                {session.channel === "whatsapp" ? (
                  <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <Smartphone className="h-3 w-3" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </span>
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    <Globe className="h-3 w-3" />
                    <span className="hidden sm:inline">Web</span>
                  </span>
                )}
              </div>
              {session.student?.phoneNumber && (
                <p className="text-xs text-gray-500">{session.student.phoneNumber}</p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <div
              className="flex items-center gap-2"
              title={
                isOutside24hWindow(session)
                  ? "Outside the 24h WhatsApp window — the student's last message was over 24h ago, so free-form messages (manual or AI) can't be sent until they message again."
                  : undefined
              }
            >
              {/* The orange banner below already says manual mode is on, so the
                  label is the first thing to drop when space is tight. */}
              <span className="hidden sm:inline text-sm text-gray-600 font-medium">
                Manual Mode
              </span>
              <ToggleSwitch
                checked={session.isManualMode}
                onChange={(val) => void handleToggle(val)}
                disabled={togglingMode || isOutside24hWindow(session)}
              />
            </div>
            <button
              onClick={() => setShowContext((s) => !s)}
              title="Student context & steering"
              className={cn(
                "p-1.5 rounded-md transition-colors",
                showContext
                  ? "bg-orange-100 text-orange-600"
                  : "text-gray-400 hover:text-gray-600 hover:bg-gray-100",
              )}
            >
              <ClipboardList className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Student context & steering panel */}
        {showContext && (
          <div className="border-b border-gray-200 bg-gray-50 px-3 sm:px-5 py-4 space-y-4 max-h-[40vh] overflow-y-auto">
            {/* Summary */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                Summary
              </h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {context?.summary?.trim() || (
                  <span className="italic text-gray-400">
                    No summary yet — builds up as the conversation grows.
                  </span>
                )}
              </p>
            </div>

            {/* AI Direction */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                  <Compass className="h-3.5 w-3.5" />
                  AI Direction
                </h4>
                <button
                  onClick={() => void handleRefreshDirection()}
                  disabled={refreshingDirection}
                  className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 disabled:opacity-50"
                >
                  <RefreshCw
                    className={cn(
                      "h-3.5 w-3.5",
                      refreshingDirection && "animate-spin",
                    )}
                  />
                  Refresh now
                </button>
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {context?.aiDirection?.trim() || (
                  <span className="italic text-gray-400">
                    No direction set yet.
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Last refreshed: {formatRefreshedAt(context?.directionUpdatedAt ?? null)}
              </p>
            </div>

            {/* Counsellor instruction (editable) */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                L2 Guidelines
              </h4>
              <p className="text-xs text-gray-500 mb-2">
                Steers the bot for THIS student — it overrides default behavior.
              </p>
              <textarea
                value={instructionDraft}
                onChange={(e) => setInstructionDraft(e.target.value)}
                rows={3}
                placeholder="e.g. Student is irritated by repeated questions. Stop pushing; only answer what they ask."
                className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <button
                onClick={() => void handleSaveInstruction()}
                disabled={
                  savingInstruction ||
                  instructionDraft === (context?.counselorInstruction ?? "")
                }
                className="mt-2 px-4 py-1.5 rounded-lg text-white text-sm bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingInstruction ? "Saving..." : "Save instruction"}
              </button>
            </div>
          </div>
        )}

        {/* Manual mode banner */}
        {session.isManualMode && (
          <div className="bg-orange-50 border-b border-orange-200 px-3 sm:px-5 py-2 text-xs text-orange-700 font-medium">
            Manual mode is ON — AI responses are paused. You are replying as Gyanplug Team.
          </div>
        )}

        {/* Legend — bubble color is the only cue for AI vs manual since both sit on the right */}
        <div className="flex items-center gap-4 px-3 sm:px-5 py-1.5 border-b border-gray-100 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            AI
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
            Manual
          </span>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-3"
        >
          {loadingMessages ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner size="md" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No messages yet</div>
          ) : (
            (() => {
              let lastDateKey = "";
              return messages.map((msg) => {
                const isAdmin = isAdminMessage(msg);
                const isStudent = isStudentMessage(msg);
                const dateKey = getDateKey(msg);
                const showDateHeader = !!dateKey && dateKey !== lastDateKey;
                if (showDateHeader) lastDateKey = dateKey;
                const raw = msg.timestamp || msg.sentAt;

                return (
                  <React.Fragment key={msg.id}>
                    {showDateHeader && raw && (
                      <div className="flex justify-center my-2">
                        <span className="bg-gray-100 text-gray-500 text-xs font-medium px-3 py-1 rounded-full">
                          {getDateHeaderLabel(raw)}
                        </span>
                      </div>
                    )}
                    <div
                      className={cn("flex flex-col", isStudent ? "items-start" : "items-end")}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap",
                          isStudent
                            ? "bg-gray-100 text-gray-900 rounded-tl-sm"
                            : isAdmin
                              ? "bg-orange-500 text-white rounded-tr-sm"
                              : "bg-primary text-white rounded-tr-sm",
                        )}
                      >
                        {renderMessageBody(msg, isAdmin)}
                      </div>
                      <span className="text-xs text-gray-400 mt-1 px-1">
                        {getMessageTime(msg)}
                      </span>
                    </div>
                  </React.Fragment>
                );
              });
            })()
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="px-3 sm:px-5 py-3 sm:py-4 border-t border-gray-200">
          {!session.isManualMode && (
            <p className="text-xs text-gray-500 mb-2 text-center">
              Enable Manual Mode to reply as Gyanplug Team
            </p>
          )}
          {session.isManualMode && !canSendManually && (
            <p className="text-xs text-red-500 mb-2 text-center">
              It's been over 24h since the student's last message — WhatsApp
              only allows template messages until they message again.
            </p>
          )}
          {fileError && (
            <p className="text-xs text-red-500 mb-2">{fileError}</p>
          )}
          {sendError && (
            <p className="text-xs text-red-500 mb-2">{sendError}</p>
          )}
          {(attachment || uploadingFile) && (
            <div className="mb-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              {uploadingFile ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="text-xs text-gray-500">Uploading…</span>
                </>
              ) : attachment ? (
                <>
                  {attachment.isImage && attachment.previewUrl ? (
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.fileName}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <FileText className="h-5 w-5 text-gray-500" />
                  )}
                  <span className="flex-1 truncate text-xs text-gray-700">
                    {attachment.fileName}
                  </span>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Remove attachment"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : null}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,text/plain"
            className="hidden"
            onChange={(e) => void handleFileSelect(e)}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canSendManually || sending || uploadingFile}
              aria-label="Attach a file"
              title={
                session.isManualMode && !canSendManually
                  ? "Outside the 24h WhatsApp window — can't send until the student messages again"
                  : undefined
              }
              className={cn(
                "self-end p-2 rounded-lg border transition-colors",
                canSendManually && !sending && !uploadingFile
                  ? "border-gray-300 text-gray-500 hover:bg-gray-100"
                  : "border-gray-200 text-gray-300 cursor-not-allowed",
              )}
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={!canSendManually || sending}
              title={
                session.isManualMode && !canSendManually
                  ? "Outside the 24h WhatsApp window — can't send until the student messages again"
                  : undefined
              }
              placeholder={
                canSendManually
                  ? "Type your message as Gyanplug Team..."
                  : session.isManualMode
                    ? "Outside the 24h WhatsApp window — can't send"
                    : "Manual mode is off"
              }
              rows={2}
              className={cn(
                "min-w-0 flex-1 resize-none rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400",
                canSendManually
                  ? "border-gray-300 bg-white"
                  : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <button
              onClick={() => void handleSend()}
              disabled={
                !canSendManually ||
                (!text.trim() && !attachment) ||
                sending ||
                uploadingFile
              }
              aria-label="Send"
              className={cn(
                "self-end shrink-0 px-3 sm:px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-1",
                canSendManually &&
                  (text.trim() || attachment) &&
                  !sending &&
                  !uploadingFile
                  ? "bg-orange-500 hover:bg-orange-600"
                  : "bg-gray-300 cursor-not-allowed",
              )}
            >
              {sending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {/* Icon alone on phones — the textarea needs the width more. */}
                  <span className="hidden sm:inline">Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiSessionChatModal;
